// Кэш холодного старта (HQ-842): последние ответы config, главной и «Матчей» (день якоря) — в localStorage, чтобы на
// медленной сети первый экран рисовался сразу, а не скелетом. Свежий запрос идёт параллельно и заменяет сохранённое.
// Ключ — `<вид>:<схема>:<версия сборки>:<язык>[:<адрес>]`: другая сборка или язык — промах, а запись вида с чужим ключом
// чистится (одна запись на вид). Запись старше суток не показывается и удаляется. localStorage недоступен
// (приватный режим, исключение на getItem/setItem) — молча без кэша, как до HQ-842.
// Вид news (HQ-1907) — только tab_bar хаба новостей: без сети и при ошибке полоса вкладок раздела остаётся.
import { lang } from '../i18n/i18n';

/** Схема записи: поднимать при смене формы типов ответа (HomeScreen, MatchesScreen, AppConfig). */
const SCHEMA = 'v1';
const VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev';
export const START_TTL_MS = 24 * 60 * 60_000;

export type StartKind = 'config' | 'home' | 'matches' | 'news';
type Entry<T> = { saved_at: number; data: T };
export type Saved<T> = { data: T; savedAt: number };

export function startKey(kind: StartKind, suffix?: string): string {
  return [kind, SCHEMA, VERSION, lang, ...(suffix ? [suffix] : [])].join(':');
}

const kindOf = (key: string) => key.slice(0, key.indexOf(':') + 1);

/** Удалить записи вида с другим ключом: прежний язык, прежняя сборка или схема, прежний адрес. */
function purgeKind(kind: string, keep: string | null): void {
  const stale: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key !== keep && key.startsWith(kind)) stale.push(key);
  }
  for (const key of stale) localStorage.removeItem(key);
}

let purged = false;
/** Один раз за запуск: записи прежнего языка/сборки не нужны — чистим, не дожидаясь свежего ответа. */
function purgeForeign(): void {
  if (purged) return;
  purged = true;
  const stale: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key) continue;
    const kind = (['config', 'home', 'matches', 'news'] as const).find((k) => key.startsWith(`${k}:`));
    if (kind && key !== startKey(kind) && !key.startsWith(`${startKey(kind)}:`)) stale.push(key);
  }
  for (const key of stale) localStorage.removeItem(key);
}

/** Сохранённый ответ или undefined: нет записи, запись битая, старше суток (удаляется) или хранилище недоступно. */
export function readStart<T>(key: string, now = Date.now()): Saved<T> | undefined {
  try {
    purgeForeign();
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as Partial<Entry<T>> | null;
    const savedAt = entry?.saved_at;
    if (typeof savedAt !== 'number' || entry?.data == null || now - savedAt > START_TTL_MS || savedAt > now + 60_000) {
      localStorage.removeItem(key);
      return undefined;
    }
    return { data: entry.data, savedAt };
  } catch {
    return undefined;
  }
}

/** Сохранить свежий ответ; прочие записи этого вида удаляются. Переполнение и приватный режим — без кэша. */
export function writeStart<T>(key: string, data: T, now = Date.now()): void {
  try {
    purgeKind(kindOf(key), key);
    localStorage.setItem(key, JSON.stringify({ saved_at: now, data } satisfies Entry<T>));
  } catch { /* приватный режим или нет места — без кэша */ }
}
